import { Grid } from 'components/grid'
import Icon from 'components/icon'
import ProgressBar from 'components/progress-bar'
import { twColor } from '@/utils/style'
import { computed, defineComponent } from 'vue'
import type { UseProviderImport } from '../hooks/useProviderImport'


export const ImportDifference = defineComponent<{
  hook: UseProviderImport
}>(
  (props) => {
    const stats = computed(() => props.hook.stats.value)

    const isFirst = computed(() => (props.hook.providerImport.value?.runCount || 0) <= 0)

    return () => {
      return (
        <div class="w-[228px] flex-col justify-start items-start gap-3 inline-flex">
          {stats.value.newInvestmentCount > 0 && !isFirst.value && (
            <div class="self-stretch justify-between items-center inline-flex">
              <div class="text-gray-500 text-sm font-medium font-['Inter'] leading-[21px]">
                New Assets
              </div>
              <div class="text-right text-gray-500 text-sm font-medium font-['Inter'] leading-[21px]">
                {'+'}
                {stats.value.newInvestmentCount}
              </div>
            </div>
          )}
          {stats.value.newLiabilityCount > 0 && !isFirst.value && (
            <div class="self-stretch justify-between items-center inline-flex">
              <div class="text-gray-500 text-sm font-medium font-['Inter'] leading-[21px]">
                New Liabilities
              </div>
              <div class="text-right text-gray-500 text-sm font-medium font-['Inter'] leading-[21px]">
                {'+'}
                {stats.value.newLiabilityCount}
              </div>
            </div>
          )}
          {stats.value.deletedInvestmentCount > 0 && !isFirst.value && (
            <div class="self-stretch justify-between items-center inline-flex">
              <div class="text-gray-500 text-sm font-medium font-['Inter'] leading-[21px]">
                Excluded Assets
              </div>
              <div class="text-right text-gray-500 text-sm font-medium font-['Inter'] leading-[21px]">
                {'-'}
                {stats.value.deletedInvestmentCount}
              </div>
            </div>
          )}
          {stats.value.deletedLiabilityCount > 0 && !isFirst.value && (
            <div class="self-stretch justify-between items-center inline-flex">
              <div class="text-gray-500 text-sm font-medium font-['Inter'] leading-[21px]">
                Excluded Liabilities
              </div>
              <div class="text-right text-gray-500 text-sm font-medium font-['Inter'] leading-[21px]">
                {'-'}
                {stats.value.deletedLiabilityCount}
              </div>
            </div>
          )}
        </div>
      )
    }
  },
  { props: ['hook'] }
)


export const SavingStep = defineComponent<{
  hook: UseProviderImport
}>(
  (props) => {
    // Count unique assets by extracting externalId from position IDs (format: "externalId::accountId")
    const investmentCount = computed(() => {
      const positionIds = props.hook.providerImport.value?.selected?.investmentIds || [];
      const uniqueExternalIds = new Set(positionIds.map(posId => posId.split('::')[0]));
      return uniqueExternalIds.size;
    });

    const liabilityCount = computed(() => {
      const positionIds = props.hook.providerImport.value?.selected?.liabilityIds || [];
      const uniqueExternalIds = new Set(positionIds.map(posId => posId.split('::')[0]));
      return uniqueExternalIds.size;
    });

    const title = computed(() => {
      if (investmentCount.value > 0 && liabilityCount.value > 0) {
        return `Saving ${investmentCount.value} assets and ${liabilityCount.value} liabilities...`;
      } else if (investmentCount.value > 0) {
        return `Saving ${investmentCount.value} assets...`;
      } else if (liabilityCount.value > 0) {
        return `Saving ${liabilityCount.value} liabilities...`;
      }
      return `Saving...`;
    })

    return () => {
      return (
        <div class="w-full flex flex-col justify-center items-center h-full">
          <div class="w-[424px] space-y-8">
            <Grid noDefaultLayout rows="max-content minmax(max-content, 1fr) max-content">
              <div class="flex items-center justify-center w-full">
                <div class="w-[79px] h-[79px] bg-primary-100 flex-none">
                  <Icon
                    class="w-[79px] h-[79px] flex items-center justify-center flex-none"
                    size="2rem"
                    fill={twColor('primary-500')}
                    src={async () => import('assets/img/svg/icons/briefcase.svg?raw')}
                  />
                </div>
              </div>
              <div class="py-8 w-full flex justify-center">
                <ProgressBar
                  class="w-[150px]"
                  withBackground
                  size="s"
                  square
                  value={props.hook.saveProgress}
                  scale100
                />
              </div>
              <div class="flex items-center justify-center">
                <div class="text-[#111928] text-base font-semibold font-['Inter'] leading-normal">
                  {title.value}
                </div>
              </div>
            </Grid>
            <div class="w-full flex items-center justify-center">
              <ImportDifference hook={props.hook} />
            </div>
          </div>
        </div>
      )
    }
  },
  { props: ['hook'] }
)
